/**
 * 预警提醒组件
 * 基于农历节假日和天气预报进行运营预警提醒
 */
<template>
  <div class="alert-card" v-if="alerts.length > 0">
    <div class="alert-header">
      <i class="el-icon-warning"></i>
      <span>运营预警</span>
      <span class="alert-count">{{ alerts.length }}</span>
    </div>
    <div class="alert-list">
      <div 
        class="alert-item" 
        v-for="(alert, index) in alerts" 
        :key="index"
        :class="alert.level">
        <div class="alert-icon">
          <i :class="alert.icon"></i>
        </div>
        <div class="alert-content">
          <div class="alert-title">{{ alert.title }}</div>
          <div class="alert-desc">{{ alert.description }}</div>
        </div>
        <div class="alert-tag">
          <el-tag :type="getTagType(alert.level)" size="mini">{{ alert.levelText }}</el-tag>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AlertCard',
  props: {
    weather: {
      type: Object,
      default: () => ({})
    },
    forecast: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      lunarHolidays: [],
      solarHolidays: []
    };
  },
  computed: {
    alerts() {
      const alertList = [];
      
      // 检测恶劣天气预警
      this.checkWeatherAlerts(alertList);
      
      // 检测节假日预警
      this.checkHolidayAlerts(alertList);
      
      // 按级别排序：danger > warning > info
      const levelOrder = { danger: 0, warning: 1, info: 2 };
      return alertList.sort((a, b) => levelOrder[a.level] - levelOrder[b.level]);
    }
  },
  methods: {
    getTagType(level) {
      const typeMap = {
        danger: 'danger',
        warning: 'warning',
        info: ''
      };
      return typeMap[level] || '';
    },
    checkWeatherAlerts(alertList) {
      // 检测当前天气
      if (this.weather && this.weather.weather) {
        const currentWeather = this.weather.weather;
        const weatherAlert = this.analyzeWeather(currentWeather, '今日');
        if (weatherAlert) {
          alertList.push(weatherAlert);
        }
      }
      
      // 检测未来天气预报
      if (this.forecast && this.forecast.length > 0) {
        this.forecast.forEach((item, index) => {
          if (index === 0) return; // 跳过今天
          
          const dayWeather = item.dayWeather || '';
          const nightWeather = item.nightWeather || '';
          const dateStr = this.formatForecastDate(item.date, item.week);
          
          // 检测白天天气
          const dayAlert = this.analyzeWeather(dayWeather, dateStr);
          if (dayAlert && !alertList.some(a => a.title === dayAlert.title && a.date === dateStr)) {
            dayAlert.date = dateStr;
            alertList.push(dayAlert);
          }
          
          // 检测夜间天气（如果与白天不同且也是恶劣天气）
          if (nightWeather !== dayWeather) {
            const nightAlert = this.analyzeWeather(nightWeather, `${dateStr}夜间`);
            if (nightAlert && !alertList.some(a => a.title === nightAlert.title && a.date === dateStr)) {
              nightAlert.date = dateStr;
              alertList.push(nightAlert);
            }
          }
        });
      }
    },
    analyzeWeather(weather, datePrefix) {
      if (!weather) return null;
      
      // 恶劣天气类型及其预警级别
      const severeWeatherPatterns = [
        { pattern: /暴雨|大暴雨|特大暴雨/, level: 'danger', levelText: '高风险', icon: 'el-icon-heavy-rain', title: '暴雨预警', desc: '可能导致道路积水、能见度降低，建议提前调整班次' },
        { pattern: /暴雪|大雪/, level: 'danger', levelText: '高风险', icon: 'el-icon-ice-cream-round', title: '暴雪预警', desc: '道路可能结冰湿滑，建议提前做好停运准备' },
        { pattern: /台风/, level: 'danger', levelText: '高风险', icon: 'el-icon-cloudy', title: '台风预警', desc: '强风暴雨天气，建议提前安排停运' },
        { pattern: /冰雹/, level: 'danger', levelText: '高风险', icon: 'el-icon-ice-cream', title: '冰雹预警', desc: '可能造成车辆损坏，建议车辆避险停放' },
        { pattern: /大雾|浓雾|特强浓雾/, level: 'danger', levelText: '高风险', icon: 'el-icon-cloudy', title: '大雾预警', desc: '能见度极低，高速可能封闭，建议调整发车计划' },
        { pattern: /中雨|雷阵雨/, level: 'warning', levelText: '中风险', icon: 'el-icon-heavy-rain', title: '降雨提醒', desc: '道路湿滑，提醒司机注意行车安全' },
        { pattern: /小雪|中雪/, level: 'warning', levelText: '中风险', icon: 'el-icon-ice-cream-round', title: '降雪提醒', desc: '道路可能湿滑，提醒司机减速慢行' },
        { pattern: /雾|霾/, level: 'warning', levelText: '中风险', icon: 'el-icon-cloudy', title: '雾霾提醒', desc: '能见度降低，提醒司机开启雾灯' },
      ];
      
      for (const item of severeWeatherPatterns) {
        if (item.pattern.test(weather)) {
          return {
            level: item.level,
            levelText: item.levelText,
            icon: item.icon,
            title: `${datePrefix}${item.title}`,
            description: `${weather}，${item.desc}`
          };
        }
      }
      
      return null;
    },
    checkHolidayAlerts(alertList) {
      const now = new Date();
      const lunarInfo = this.getLunarInfo(now);
      
      // 检测农历节假日
      const lunarHoliday = this.checkLunarHoliday(lunarInfo.month, lunarInfo.day);
      if (lunarHoliday) {
        alertList.push(lunarHoliday);
      }
      
      // 检测公历节假日
      const solarHoliday = this.checkSolarHoliday(now.getMonth() + 1, now.getDate());
      if (solarHoliday) {
        alertList.push(solarHoliday);
      }
      
      // 检测即将到来的节假日（未来7天）
      for (let i = 1; i <= 7; i++) {
        const futureDate = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
        const futureLunar = this.getLunarInfo(futureDate);
        const futureLunarHoliday = this.checkLunarHoliday(futureLunar.month, futureLunar.day, i);
        if (futureLunarHoliday && !alertList.some(a => a.holidayName === futureLunarHoliday.holidayName)) {
          alertList.push(futureLunarHoliday);
        }
        
        const futureSolarHoliday = this.checkSolarHoliday(futureDate.getMonth() + 1, futureDate.getDate(), i);
        if (futureSolarHoliday && !alertList.some(a => a.holidayName === futureSolarHoliday.holidayName)) {
          alertList.push(futureSolarHoliday);
        }
      }
    },
    checkLunarHoliday(month, day, daysAhead = 0) {
      // 农历节假日配置
      const lunarHolidays = [
        { month: 1, day: 1, name: '春节', level: 'danger', desc: '春运高峰期，运力需求激增，建议提前调配车辆和司机' },
        { month: 1, day: 15, name: '元宵节', level: 'warning', desc: '出行需求增加，建议适当增加班次' },
        { month: 5, day: 5, name: '端午节', level: 'warning', desc: '小长假出行高峰，建议提前安排运力' },
        { month: 8, day: 15, name: '中秋节', level: 'warning', desc: '返乡探亲高峰，建议增加热门线路班次' },
        { month: 9, day: 9, name: '重阳节', level: 'info', desc: '老年人出行增多，注意服务质量' },
        { month: 12, day: 30, name: '除夕', level: 'danger', desc: '春运返乡高峰，建议全力保障运力' }
      ];
      
      // 春节前后特殊处理（腊月二十三到正月十五）
      if (month === 12 && day >= 23) {
        return {
          level: 'danger',
          levelText: '高风险',
          icon: 'el-icon-date',
          title: daysAhead > 0 ? `${daysAhead}天后进入春运` : '春运期间',
          description: '春运高峰期，客流量大幅增加，请提前做好运力调配',
          holidayName: '春运'
        };
      }
      
      for (const holiday of lunarHolidays) {
        if (holiday.month === month && holiday.day === day) {
          return {
            level: holiday.level,
            levelText: holiday.level === 'danger' ? '高风险' : holiday.level === 'warning' ? '中风险' : '低风险',
            icon: 'el-icon-date',
            title: daysAhead > 0 ? `${daysAhead}天后${holiday.name}` : `今日${holiday.name}`,
            description: holiday.desc,
            holidayName: holiday.name
          };
        }
        
        // 检测节假日前一天
        if (holiday.month === month && holiday.day === day + 1 && daysAhead === 0) {
          return {
            level: 'warning',
            levelText: '中风险',
            icon: 'el-icon-date',
            title: `明日${holiday.name}`,
            description: `${holiday.name}前夕，出行需求开始增加，建议提前准备`,
            holidayName: holiday.name
          };
        }
      }
      
      return null;
    },
    checkSolarHoliday(month, day, daysAhead = 0) {
      // 公历节假日配置
      const solarHolidays = [
        { month: 1, day: 1, name: '元旦', level: 'warning', desc: '元旦假期，出行需求增加' },
        { month: 5, day: 1, name: '劳动节', level: 'warning', desc: '五一假期，旅游出行高峰' },
        { month: 10, day: 1, name: '国庆节', level: 'danger', desc: '国庆黄金周，出行需求激增，建议全力保障运力' }
      ];
      
      // 国庆假期特殊处理（10月1日-7日）
      if (month === 10 && day >= 1 && day <= 7) {
        return {
          level: 'danger',
          levelText: '高风险',
          icon: 'el-icon-date',
          title: daysAhead > 0 ? `${daysAhead}天后国庆假期` : '国庆假期',
          description: '国庆黄金周，客流量大幅增加，请做好运力保障',
          holidayName: '国庆节'
        };
      }
      
      for (const holiday of solarHolidays) {
        if (holiday.month === month && holiday.day === day) {
          return {
            level: holiday.level,
            levelText: holiday.level === 'danger' ? '高风险' : holiday.level === 'warning' ? '中风险' : '低风险',
            icon: 'el-icon-date',
            title: daysAhead > 0 ? `${daysAhead}天后${holiday.name}` : `今日${holiday.name}`,
            description: holiday.desc,
            holidayName: holiday.name
          };
        }
      }
      
      return null;
    },
    getLunarInfo(date) {
      const lunarInfo = [
        0x04bd8, 0x04ae0, 0x0a570, 0x054d5, 0x0d260, 0x0d950, 0x16554, 0x056a0, 0x09ad0, 0x055d2,
        0x04ae0, 0x0a5b6, 0x0a4d0, 0x0d250, 0x1d255, 0x0b540, 0x0d6a0, 0x0ada2, 0x095b0, 0x14977,
        0x04970, 0x0a4b0, 0x0b4b5, 0x06a50, 0x06d40, 0x1ab54, 0x02b60, 0x09570, 0x052f2, 0x04970,
        0x06566, 0x0d4a0, 0x0ea50, 0x06e95, 0x05ad0, 0x02b60, 0x186e3, 0x092e0, 0x1c8d7, 0x0c950,
        0x0d4a0, 0x1d8a6, 0x0b550, 0x056a0, 0x1a5b4, 0x025d0, 0x092d0, 0x0d2b2, 0x0a950, 0x0b557,
        0x06ca0, 0x0b550, 0x15355, 0x04da0, 0x0a5d0, 0x14573, 0x052d0, 0x0a9a8, 0x0e950, 0x06aa0,
        0x0aea6, 0x0ab50, 0x04b60, 0x0aae4, 0x0a570, 0x05260, 0x0f263, 0x0d950, 0x05b57, 0x056a0,
        0x096d0, 0x04dd5, 0x04ad0, 0x0a4d0, 0x0d4d4, 0x0d250, 0x0d558, 0x0b540, 0x0b5a0, 0x195a6,
        0x095b0, 0x049b0, 0x0a974, 0x0a4b0, 0x0b27a, 0x06a50, 0x06d40, 0x0af46, 0x0ab60, 0x09570,
        0x04af5, 0x04970, 0x064b0, 0x074a3, 0x0ea50, 0x06b58, 0x055c0, 0x0ab60, 0x096d5, 0x092e0,
        0x0c960, 0x0d954, 0x0d4a0, 0x0da50, 0x07552, 0x056a0, 0x0abb7, 0x025d0, 0x092d0, 0x0cab5,
        0x0a950, 0x0b4a0, 0x0baa4, 0x0ad50, 0x055d9, 0x04ba0, 0x0a5b0, 0x15176, 0x052b0, 0x0a930,
        0x07954, 0x06aa0, 0x0ad50, 0x05b52, 0x04b60, 0x0a6e6, 0x0a4e0, 0x0d260, 0x0ea65, 0x0d530,
        0x05aa0, 0x076a3, 0x096d0, 0x04afb, 0x04ad0, 0x0a4d0, 0x1d0b6, 0x0d250, 0x0d520, 0x0dd45,
        0x0b5a0, 0x056d0, 0x055b2, 0x049b0, 0x0a577, 0x0a4b0, 0x0aa50, 0x1b255, 0x06d20, 0x0ada0
      ];

      const lYearDays = (y) => {
        let sum = 348;
        for (let i = 0x8000; i > 0x8; i >>= 1) {
          sum += (lunarInfo[y - 1900] & i) ? 1 : 0;
        }
        return sum + this.leapDays(y, lunarInfo);
      };

      this.leapDays = (y, info) => {
        if (this.leapMonth(y, info)) {
          return (info[y - 1900] & 0x10000) ? 30 : 29;
        }
        return 0;
      };

      this.leapMonth = (y, info) => {
        return info[y - 1900] & 0xf;
      };

      const monthDays = (y, m, info) => {
        return (info[y - 1900] & (0x10000 >> m)) ? 30 : 29;
      };

      let offset = Math.floor((date - new Date(1900, 0, 31)) / 86400000);
      let year = 1900;
      let temp = 0;

      for (year = 1900; year < 2100 && offset > 0; year++) {
        temp = lYearDays(year);
        offset -= temp;
      }
      if (offset < 0) {
        offset += temp;
        year--;
      }

      const leap = this.leapMonth(year, lunarInfo);
      let isLeap = false;
      let month = 1;

      for (month = 1; month < 13 && offset > 0; month++) {
        if (leap > 0 && month === (leap + 1) && !isLeap) {
          --month;
          isLeap = true;
          temp = this.leapDays(year, lunarInfo);
        } else {
          temp = monthDays(year, month, lunarInfo);
        }
        if (isLeap && month === (leap + 1)) isLeap = false;
        offset -= temp;
      }

      if (offset === 0 && leap > 0 && month === leap + 1) {
        if (isLeap) {
          isLeap = false;
        } else {
          isLeap = true;
          --month;
        }
      }
      if (offset < 0) {
        offset += temp;
        --month;
      }

      return {
        year,
        month,
        day: offset + 1,
        isLeap
      };
    },
    formatForecastDate(dateStr, week) {
      if (!dateStr) return '';
      const weekMap = { '1': '周一', '2': '周二', '3': '周三', '4': '周四', '5': '周五', '6': '周六', '7': '周日' };
      const parts = dateStr.split('-');
      return `${parts[1]}/${parts[2]}(${weekMap[week] || ''})`;
    }
  }
};
</script>

<style lang="less" scoped>
.alert-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  .alert-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f0f0;

    i {
      font-size: 18px;
      color: #E6A23C;
    }

    span {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }

    .alert-count {
      background: #E6A23C;
      color: #fff;
      font-size: 12px;
      font-weight: 500;
      padding: 2px 8px;
      border-radius: 10px;
      margin-left: auto;
    }
  }

  .alert-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 280px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #dcdfe6;
      border-radius: 2px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    .alert-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px;
      border-radius: 8px;
      transition: all 0.2s ease;
/*
      &:hover {
        //transform: translateX(4px);
      }*/

      &.danger {
        background: rgba(245, 108, 108, 0.08);
        border-left: 3px solid #F56C6C;

        .alert-icon i {
          color: #F56C6C;
        }
      }

      &.warning {
        background: rgba(230, 162, 60, 0.08);
        border-left: 3px solid #E6A23C;

        .alert-icon i {
          color: #E6A23C;
        }
      }

      &.info {
        background: rgba(43, 119, 255, 0.08);
        border-left: 3px solid #2B77FF;

        .alert-icon i {
          color: #2B77FF;
        }
      }

      .alert-icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.8);

        i {
          font-size: 16px;
        }
      }

      .alert-content {
        flex: 1;
        min-width: 0;

        .alert-title {
          font-size: 14px;
          font-weight: 600;
          color: #303133;
          margin-bottom: 4px;
        }

        .alert-desc {
          font-size: 12px;
          color: #909399;
          line-height: 1.5;
        }
      }

      .alert-tag {
        flex-shrink: 0;
      }
    }
  }
}
</style>
